import axios from 'axios';
import clsx from 'clsx';
import { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { PlansResponse } from '@flintmint/core-shared/billing';
import { formatCount, formatTypicalNotes, pluralize } from '@flintmint/core-shared/utils';
import { Button } from '@flintmint/web-shared/components/button';
import { Input } from '@flintmint/web-shared/components/input';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { usePlans } from '@/hooks/use-plans';
import { useUserAccount } from '@/hooks/use-user-account';
import { useAppSettings } from '@/hooks/use-app-settings';
import { appRoutes } from '@/constants/routes';
import { urlOverlays } from '@/constants/url-overlays';
import { FullScreenOverlay, FullScreenOverlayPresence, OverlayPanel } from '@/components/full-screen-overlay';

import { canBuyRecharge } from '../billing-state';
import { formatPrice } from '../billing-format';
import { markRechargeCheckout } from '../recharge-return';
import { type SkeletonLineStyle, Text, TextSkeleton, type TextStyle } from '../billing-text';
import { useReturnFocus } from '../use-return-focus';
import { useStripeRedirect } from '../use-stripe-redirect';

import styles from './recharge.module.scss';

/** Mounted once over every screen; opened by `useUrlOverlay(urlOverlays.recharge, urlOverlays)`. */
export const Recharge = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.recharge, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <RechargeGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/** The top-up session answers 409 unless the account can buy Recharge, so any other account closes the modal unseen. */
const RechargeGate = ({ onClose }: { onClose: () => void }) => {
  const { account, loading } = useUserAccount();
  const denied = !loading && !canBuyRecharge(account);

  useEffect(() => {
    if (denied) onClose();
  }, [denied, onClose]);

  return (
    !denied && <RechargeDialog planName={account?.stripeSubscription?.planName} accountLoading={loading} onClose={onClose} />
  );
};

type Choice = { stripeProductId: string; dollars?: number; credits: number; amountCents: number };

type RechargeDialogProps = { planName: string | undefined; accountLoading: boolean; onClose: () => void };

/**
 * A pack or a typed whole-dollar amount, then a review before Stripe Checkout in this same overlay. The step is not
 * in the URL, so a reload returns to the choices. While the account or plans load, the layout renders with each
 * line in place as a skeleton, one tile per cached pack (else three).
 */
const RechargeDialog = ({ planName, accountLoading, onClose }: RechargeDialogProps) => {
  const { data, isLoading: plansLoading } = usePlans(true);
  const creditsPerTypicalNote = useAppSettings()?.creditsPerTypicalNote;
  const amountId = useId();

  const loading = accountLoading || plansLoading;
  const packs = loading ? undefined : data?.topUpPacks;
  const custom = loading ? undefined : data?.topUpCustom;
  const unavailable = !loading && !packs?.length && !custom;

  // The middle pack starts selected, and is selected again if a refetch drops the chosen one. Typing an amount
  // deselects the packs (`null`) until one is chosen again.
  const [selectedId, setSelectedId] = useState<string | null>();
  const [amount, setAmount] = useState('');
  const selectedPack =
    selectedId === null
      ? undefined
      : packs?.find((pack) => pack.stripeProductId === selectedId) ?? packs?.[Math.floor((packs.length - 1) / 2)];
  const amountCheck = custom && checkAmount(amount, custom);
  const choice: Choice | undefined = selectedPack
    ? { stripeProductId: selectedPack.stripeProductId, credits: selectedPack.credits, amountCents: selectedPack.amountCents }
    : custom && amountCheck?.valid
    ? {
        stripeProductId: custom.stripeProductId,
        dollars: Number(amount),
        credits: Number(amount) * custom.creditsPerDollar,
        amountCents: Number(amount) * 100,
      }
    : undefined;

  // The reviewed choice, which a refetch never swaps; one it drops or reprices ends the review instead.
  const [review, setReview] = useState<Choice>();
  const continueRef = useRef<HTMLButtonElement>(null);
  useReturnFocus(review !== undefined, continueRef);
  const reviewDropped = review && !loading && !(data && isChoiceOffered(review, data));
  useEffect(() => {
    if (reviewDropped) setReview(undefined);
  }, [reviewDropped]);
  const { startCheckout, starting } = useStartCheckout(onClose);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const reviewing = review && !reviewDropped ? review : undefined;
  const backToChoices = () => {
    if (!starting) setReview(undefined);
  };
  // Leaving the overlay for Subscription & Billing; a push keeps the page the user came from in history.
  const backToSubscription = () => {
    onClose();
    if (pathname !== appRoutes.settingsBilling) void navigate(appRoutes.settingsBilling);
  };

  const selectPack = (stripeProductId: string) => {
    setSelectedId(stripeProductId);
    setAmount('');
  };
  // The field shows the amount after a "$", which this strips with every other character but digits and the point.
  const typeAmount = (value: string) => {
    setSelectedId(null);
    setAmount(value.replace(/[^\d.]/g, '').replace(/^0+(?=\d)/, ''));
  };

  const showPacks = loading ? (data?.topUpPacks.length ?? PLACEHOLDER_PACK_COUNT) > 0 : Boolean(packs?.length);
  const showCustom = loading ? !data || Boolean(data.topUpCustom) : Boolean(custom);

  if (reviewing) {
    return (
      <FullScreenOverlay
        title="Recharge your Energy"
        subtitle={`Buy extra capacity for your ${planName} subscription.`}
        view="review"
        onEscape={backToChoices}
      >
        <OverlayPanel title="One-time Recharge">
          <div>{`${formatCount(reviewing.credits)} Joules · ${formatPrice(reviewing.amountCents)} USD`}</div>
          <div className={styles.reviewNote}>{`Your ${planName} plan and renewal date stay the same.`}</div>
        </OverlayPanel>
        <div className={styles.reviewActions}>
          <Button size="big" fullWidth cornerRadius="smooth" loading={starting} onClick={() => startCheckout(reviewing)}>
            Continue in Stripe
          </Button>
          <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" disabled={starting} onClick={backToSubscription}>
            Back to subscription
          </Button>
        </div>
      </FullScreenOverlay>
    );
  }

  return (
    <FullScreenOverlay
      title={
        <Text style={textStyles.title} loading={loading}>
          Keep your AI workflow moving
        </Text>
      }
      subtitle={
        <Text style={textStyles.body} loading={loading}>
          A lasting reserve for your next AI task. Monthly Energy is used first.
        </Text>
      }
      loading={loading}
      loadingLabel="Recharge"
      size="medium"
      view="choose"
      onEscape={onClose}
    >
      {unavailable ? (
        <p className={styles.failure}>Recharge can’t be loaded right now. Try again later.</p>
      ) : (
        <div className={styles.choices}>
          {showPacks && (
            <>
              <div className={styles.label}>
                <Text style={textStyles.body} loading={loading}>
                  Choose an amount
                </Text>
              </div>
              <div
                className={styles.packs}
                role={loading ? undefined : 'radiogroup'}
                aria-label={loading ? undefined : 'Recharge amounts'}
              >
                {loading &&
                  Array.from({ length: data?.topUpPacks.length ?? PLACEHOLDER_PACK_COUNT }, (_, index) => (
                    <PackTileSkeleton key={index} estimate={creditsPerTypicalNote !== undefined} />
                  ))}
                {packs?.map((pack) => (
                  <PackTile
                    key={pack.stripeProductId}
                    pack={pack}
                    creditsPerTypicalNote={creditsPerTypicalNote}
                    selected={pack.stripeProductId === selectedPack?.stripeProductId}
                    onSelect={() => selectPack(pack.stripeProductId)}
                  />
                ))}
              </div>
            </>
          )}

          {showCustom && (
            <div className={styles.custom}>
              <label htmlFor={amountId} className={styles.label}>
                <Text style={textStyles.body} loading={loading}>
                  Or enter an amount in USD
                </Text>
              </label>
              {custom ? (
                <>
                  <Input
                    id={amountId}
                    size="big"
                    appearance="outline"
                    placeholder="Enter amount"
                    inputMode="numeric"
                    clearable={false}
                    wrapperAs="div"
                    error={amountCheck?.error !== undefined}
                    aria-invalid={amountCheck?.error !== undefined}
                    aria-describedby={`${amountId}-hint`}
                    value={amount && `$${amount}`}
                    onChange={(event) => typeAmount(event.target.value)}
                  />
                  <div
                    id={`${amountId}-hint`}
                    aria-live="polite"
                    className={clsx(styles.hint, { [styles.error]: amountCheck?.error !== undefined })}
                  >
                    {amountCheck?.error ??
                      `Enter whole dollars from ${formatDollars(custom.minDollars)} to ${formatDollars(custom.maxDollars)}.`}
                  </div>
                </>
              ) : (
                <>
                  <Skeleton height={46} trueWidth="100%" borderRadius={60} />
                  <TextSkeleton style={textStyles.hint} width={skeletonWidths.hint} />
                </>
              )}
            </div>
          )}

          <div className={styles.summary}>
            {loading ? (
              <TextSkeleton
                style={textStyles.body}
                width={creditsPerTypicalNote === undefined ? skeletonWidths.summaryWithoutEstimate : skeletonWidths.summary}
              />
            ) : choice ? (
              [`${formatCount(choice.credits)} Joules`, formatTypicalNotes(choice.credits, creditsPerTypicalNote)]
                .filter(Boolean)
                .join(' · ')
            ) : (
              'Enter a valid amount to see your Joules.'
            )}
          </div>
        </div>
      )}

      <div className={styles.footnote}>
        <Text style={textStyles.footnote} loading={loading}>
          Recharge Joules do not expire while your account exists. Use them after Monthly Energy with an eligible paid
          subscription. Note estimates vary. Tax is shown at checkout.
        </Text>
      </div>

      <div className={styles.actions}>
        {loading ? (
          <Skeleton height={46} trueWidth="100%" borderRadius={16} />
        ) : (
          !unavailable && (
            <Button
              ref={continueRef}
              size="big"
              fullWidth
              cornerRadius="smooth"
              disabled={!choice}
              onClick={() => setReview(choice)}
            >
              {choice ? `Continue with ${formatPrice(choice.amountCents)}` : 'Continue to checkout'}
            </Button>
          )
        )}
        <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" onClick={onClose}>
          Not now
        </Button>
      </div>
    </FullScreenOverlay>
  );
};

/**
 * Checks the reviewed choice against freshly fetched plans before creating the session, so the Joules reviewed and
 * shown on return are the ones the backend credits from its live catalog. A changed choice leaves for no session:
 * the fetched plans end the review through `isChoiceOffered`, and a toast says why. `onClose` closes the overlay in
 * place when Checkout answers that the account cannot buy Recharge.
 */
const useStartCheckout = (onClose: () => void) => {
  const { showToastNotification } = useToaster();
  const { refetch: refetchPlans } = usePlans(true);
  const { redirect, pending } = useStripeRedirect({
    errorTitle: 'Recharge could not be opened. Try again.',
    onError: (error) => {
      if (error instanceof PricesChangedError) {
        showToastNotification({ type: 'info', title: 'Recharge prices changed. Check your amount again.' });
        return true;
      }
      if (!(axios.isAxiosError(error) && error.response?.status === 409)) return false;
      showToastNotification({ type: 'info', title: 'Recharge needs an active subscription' });
      onClose();
      return true;
    },
  });

  const startCheckout = (choice: Choice) =>
    redirect(choice.stripeProductId, async (api) => {
      const { data } = await refetchPlans({ throwOnError: true });
      if (!data || !isChoiceOffered(choice, data)) throw new PricesChangedError();
      const { stripeProductId, dollars, credits, amountCents } = choice;
      const successValue = `${credits}-${amountCents}`;
      const session = await api.createTopUpSession({
        stripeProductId,
        dollars,
        successUrl: `${window.location.origin}${appRoutes.settingsBilling}?${new URLSearchParams({
          [urlOverlays.rechargeSuccess.param]: successValue,
        })}`,
        // Nothing is open on return: Subscription & Billing as it is.
        cancelUrl: `${window.location.origin}${appRoutes.settingsBilling}`,
      });
      markRechargeCheckout(successValue);
      return session;
    });

  return { startCheckout, starting: pending !== undefined };
};

class PricesChangedError extends Error {}

/** Whether the plans still offer the choice at the same Joules and price, a custom amount within the limits. */
const isChoiceOffered = (
  { stripeProductId, dollars, credits, amountCents }: Choice,
  { topUpPacks, topUpCustom }: PlansResponse
) =>
  dollars === undefined
    ? topUpPacks.some(
        (pack) => pack.stripeProductId === stripeProductId && pack.credits === credits && pack.amountCents === amountCents
      )
    : topUpCustom?.stripeProductId === stripeProductId &&
      dollars * topUpCustom.creditsPerDollar === credits &&
      checkAmount(String(dollars), topUpCustom).valid;

type TopUpCustom = NonNullable<PlansResponse['topUpCustom']>;

/** A whole dollar amount within the response's limits. An empty entry is neither valid nor an error. */
const checkAmount = (amount: string, { minDollars, maxDollars }: TopUpCustom): { valid: boolean; error?: string } => {
  if (amount === '') return { valid: false };
  if (amount.includes('.')) return { valid: false, error: 'Enter a whole-dollar amount, such as $12 or $13.' };
  const dollars = Number(amount);
  if (dollars < minDollars) {
    const min = formatDollars(minDollars);
    return { valid: false, error: `Minimum purchase is ${min}. Enter ${min} or more.` };
  }
  if (dollars > maxDollars) return { valid: false, error: `Enter ${formatDollars(maxDollars)} or less.` };
  return { valid: true };
};

const formatDollars = (dollars: number) => formatPrice(dollars * 100);

type Pack = PlansResponse['topUpPacks'][number];

type PackTileProps = { pack: Pack; creditsPerTypicalNote: number | undefined; selected: boolean; onSelect: () => void };

/** A single-choice tile: a click on it or its radio (Tab into the group, then the arrow keys) selects it. */
const PackTile = ({ pack, creditsPerTypicalNote, selected, onSelect }: PackTileProps) => {
  const notes = formatPackNotes(pack.credits, creditsPerTypicalNote);

  return (
    <label className={clsx(styles.pack, { [styles.selected]: selected })}>
      <input type="radio" name="recharge-pack" className={styles.radio} checked={selected} onChange={onSelect} />
      <span>{formatPrice(pack.amountCents)}</span> <span>{`${formatCount(pack.credits)} Joules`}</span>
      {notes && (
        <>
          {' '}
          <span className={styles.packNotes}>{notes}</span>
        </>
      )}
    </label>
  );
};

/** "About 10 notes"; left out while the app settings load. */
const formatPackNotes = (joules: number, creditsPerTypicalNote: number | undefined) =>
  creditsPerTypicalNote === undefined ? undefined : `About ${pluralize(Math.floor(joules / creditsPerTypicalNote), 'note')}`;

/** A tile's layout with a skeleton in place of each line, none marked selected so no highlight jumps when the packs load. */
const PackTileSkeleton = ({ estimate }: { estimate: boolean }) => (
  <div className={styles.pack} data-testid="recharge-pack-skeleton">
    <TextSkeleton style={textStyles.pack} width={skeletonWidths.packPrice} />
    <TextSkeleton style={textStyles.pack} width={skeletonWidths.packJoules} />
    {estimate && <TextSkeleton style={textStyles.pack} width={skeletonWidths.packNotes} />}
  </div>
);

/** Three tiles, shown while loading when no plans response is cached to count. */
const PLACEHOLDER_PACK_COUNT = 3;

// The title's style is the plan picker's; the body's and footnote's glyph widths are Inter's average at 14 and 12.
const textStyles = {
  title: { lineHeight: 42, textHeight: 20, charWidth: 13.4 },
  body: { lineHeight: 21, textHeight: 10, charWidth: 6.4 },
  pack: { lineHeight: 22, textHeight: 11 },
  hint: { lineHeight: 18, textHeight: 8 },
  footnote: { lineHeight: 18, textHeight: 8, charWidth: 5.5 },
} satisfies Record<string, TextStyle | SkeletonLineStyle>;

// Loading widths of the data-dependent lines, estimated in Inter at their text styles: a price "$100" about 40;
// "1,000 Joules" about 95; "About 50 notes" about 100; the hint "Enter whole dollars from $10 to $500."
// about 210; the summary "1,000 Joules · About 50 typical notes" about 245, and "1,000 Joules" alone about 80.
const skeletonWidths = {
  packPrice: 40,
  packJoules: 95,
  packNotes: 100,
  hint: 210,
  summary: 245,
  summaryWithoutEstimate: 80,
};
